import {Heading} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';

import {head, isEmpty} from 'ramda';

import {useGetAuctionUserHiddenQuery, useSourcingListUserHiddenQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {COUNTRY_CZE, CURRENCY_CZK, formatDocumentTitle} from '@dms/shared';

import {AuctionPreview} from '../../components/AuctionPreview/AuctionPreview';
import {SourcingDataStatus} from '../../components/SourcingDataStatus/SourcingDataStatus';
import {SourcingLayout} from '../../components/SourcingLayout/SourcingLayout';
import {useSourcingTable} from '../../components/SourcingTable/hooks/useSourcingTable';
import {useSourcingTableColumns} from '../../components/SourcingTable/hooks/useSourcingTableColumns';
import {SourcingTable} from '../../components/SourcingTable/SourcingTable';
import {createSourcingTableQueryResult} from '../../components/SourcingTable/utils/createSourcingTableQueryResult';
import {VehiclePreview} from '../../components/VehiclePreview/VehiclePreview';
import {
  AUCTION_USER_LIST_ORDER_BY_MAP,
  AUCTION_USER_LIST_SORT_OPTIONS,
} from '../../constants/auctionSortOptions';
import {
  CLASSIFIEDS_USER_LIST_ORDER_BY_MAP,
  CLASSIFIEDS_USER_LIST_SORT_OPTIONS,
} from '../../constants/classifiedsSortBy';
import {USER_LIST_DEFAULT_ORDER_ID} from '../../constants/USER_LIST_DEFAULT_ORDER_ID';
import {useAuctionEnumLookup} from '../../hooks/useAuctionEnumLookup';
import {useSourcingCurrency} from '../../hooks/useSourcingCurrency';
import {useSourcingTargetCountry} from '../../hooks/useSourcingTargetCountry';
import {SourcingType, useSourcingType} from '../../hooks/useSourcingType';
import {getAuctionTableColumns} from '../../utils/getAuctionTableColumns';
import {getSourcingVehicleId} from '../../utils/getSourcingVehicleId';
import {isAuctionVehicle} from '../../utils/isAuctionVehicle';

export function UserHiddenPage() {
  const formatNumber = useFormatNumber();
  const enumLookup = useAuctionEnumLookup();
  const currency = useSourcingCurrency();
  const targetCountry = useSourcingTargetCountry();
  const sourcingType = useSourcingType();
  const [classifiedsState, classifiedsDispatch] = useSourcingTable(
    'user-hidden',
    USER_LIST_DEFAULT_ORDER_ID
  );
  const [auctionsState, auctionsDispatch] = useSourcingTable(
    'user-hidden-auctions',
    USER_LIST_DEFAULT_ORDER_ID
  );
  const [state, dispatch] =
    sourcingType === SourcingType.CLASSIFIEDS
      ? [classifiedsState, classifiedsDispatch]
      : [auctionsState, auctionsDispatch];

  const sortByOptions =
    sourcingType === SourcingType.AUCTIONS
      ? AUCTION_USER_LIST_SORT_OPTIONS
      : CLASSIFIEDS_USER_LIST_SORT_OPTIONS;
  const orderBy =
    sourcingType === SourcingType.AUCTIONS
      ? AUCTION_USER_LIST_ORDER_BY_MAP.get(state.orderBy)
      : CLASSIFIEDS_USER_LIST_ORDER_BY_MAP.get(state.orderBy);

  const classifiedsQueryResult = useSourcingListUserHiddenQuery(
    {
      orderBy: isEmpty(orderBy) ? undefined : orderBy?.join(','),
      currency: currency?.code ?? CURRENCY_CZK,
      buyerCountry: targetCountry?.code ?? COUNTRY_CZE,
      size: 1000,
    },
    {skip: sourcingType === SourcingType.AUCTIONS}
  );

  const auctionsQueryResult = useGetAuctionUserHiddenQuery(
    {
      orderBy: isEmpty(orderBy) ? undefined : orderBy,
      currency: currency?.code ?? CURRENCY_CZK,
      buyerCountry: targetCountry?.code ?? COUNTRY_CZE,
      size: 1000,
    },
    {skip: sourcingType === SourcingType.CLASSIFIEDS}
  );

  const sourcingColumns = useSourcingTableColumns();
  const auctionColumns = getAuctionTableColumns({formatNumber, enumLookup});
  const columns = sourcingType === SourcingType.CLASSIFIEDS ? sourcingColumns : auctionColumns;
  const tableQueryResult = createSourcingTableQueryResult({
    sourcingType,
    enumLookup,
    classifiedsQueryResult,
    auctionsQueryResult,
  });
  const rows = tableQueryResult.data;

  const selectedAdId = state.selectedRow?.sourcingVehicle.adId;
  const preview = rows.find((row) => row.sourcingVehicle.adId === selectedAdId) ?? head(rows);

  return (
    <SourcingLayout
      header={
        <Heading size={2} alternative>
          {i18n.t('entity.vehicle.labels.hiddenPage')}
        </Heading>
      }
      content={
        <SourcingDataStatus
          iconUrl="/assets/images/empty_images/empty-hidden.svg"
          text={i18n.t('entity.vehicle.notifications.nothingHidden')}
          isEmpty={!tableQueryResult.isLoading && isEmpty(rows)}
        >
          <SourcingTable
            state={state}
            dispatch={dispatch}
            columns={columns}
            sortByOptions={sortByOptions}
            queryResult={tableQueryResult}
          />
        </SourcingDataStatus>
      }
      iframe={
        sourcingType === SourcingType.AUCTIONS && isAuctionVehicle(preview) ? (
          <AuctionPreview key={preview.auctionVehicle.adId} auction={preview} />
        ) : (
          <VehiclePreview
            key={getSourcingVehicleId(preview)}
            vehicleId={getSourcingVehicleId(preview)}
          />
        )
      }
      pageTitle={formatDocumentTitle(
        i18n.t('entity.vehicle.labels.hiddenPage'),
        sourcingType === SourcingType.CLASSIFIEDS
          ? i18n.t('page.sourcing.labels.classifieds')
          : i18n.t('page.sourcing.labels.auctions'),
        i18n.t('page.sourcing.labels.sourcing')
      )}
    />
  );
}
